import dayjs from 'dayjs'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DateInput } from 'semantic-ui-calendar-react'
import { Button, Divider, Dropdown, Grid, Icon, Input, Popup, Progress, Step } from 'semantic-ui-react'
import { useDebounce } from 'use-lodash-debounce'
import MilkMoneyAccountingService, { FarmerInvoiceType } from '~/api/services/milkMoneyAccounting'
import Dialog from '~/components/Dialog'
import ExcelExport from '~/components/ExcelExport'
import Layout from '~/components/Layout'
import { LazyLottie } from '~/components/LazyLottie'
import notify from '~/components/Notify'
import TableComponent from '~/components/TableComponent'
import { useHash } from '~/components/useHash'
import { LIGHT_USER_ROLE_SLUG } from '~/Constants'
import ENV from '~/environment'
import { MilkMoneyQuery } from '~/QueryHooks/MilkMoneyQuery'
import { useAuth } from '~/routes/components/AuthProvider'
import { formatNumberWithoutFractions, getDayAhead, handleErrors, parseDate } from '~/utils/helpers'
import { FilterWrapper } from '../../DairyFactory/Farmers/style'
import { StyledGridColumns, StyledGridRow } from '../../MilkFixedPrice/ShowMilkFixedPrice/style'
import { CustomRow, StyledGrid, StyledSegment } from '../../Survey/ShowSurvey/style'
import { milkPaymentStatus } from '../Invoice/constants'
import GenerateMilkBill from '../Invoice/GenerateMilkBill'
import { ActionsWrapper, ProgressWrapper, StepDescr, StyledDimmer } from '../Invoice/style'
import CloseMonth from './CloseMonth'
import { actions, getSteps, getTableColumnHeaders } from './constants'
import CreateBankFile from './CreateBankFile'

const PartPayment: React.FC = () => {
  const { t } = useTranslation()
  const increment = useRef(null);
  const intervalPdfs = useRef(null);
  const { systemData: { languages, logged: { roleSlug, languageId } } } = useAuth()
  const lighUserLogged = roleSlug === LIGHT_USER_ROLE_SLUG
  const steps = getSteps()
  const [hash] = useHash();
  const languageLocal = languages.find(language => language.id === languageId)

  const [tableColumnHeaders, setTableColumnHeaders] = useState(getTableColumnHeaders())
  const [isOpenCloseMonthDialog, setIsOpenCloseMonthDialog] = useState(false)
  const [dataFiltered, setDataFiltered] = useState({
    month: '',
    dateFrom: null,
    dateTo: null,
    search: '',
    page: 0,
    rows: 20
  })
  const [loadingAction, setLoadingAction] = useState({
    type: '',
    id: null,
    value: false,
    idKey: ''
  })
  const [search, setSearch] = useState('')
  const [isLoadingPdfFiles, setIsLoadingPdfFiles] = useState(false)
  const [selectedMonth, setSelectedMonth] = useState('')
  const [openExportBankModal, setExportBankModal] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [isBusySepaXML, setIsBusySepaXML] = useState(false)
  const [openGeneratingModal, setOpenGeneratingModal] = useState(false)
  const [isOpenGeneratingPdfs, setIsOpenGeneratingPdfs] = useState(false)

  const debouncedValue = useDebounce(search, 300);

  const {
    data: partialPaymentSettings,
    isLoading: isBusySettings,
    isFetching: isFetchingSettings,
    refetch: fetchSettings
  } = MilkMoneyQuery.getPartialMilkMoneySettings({
    month: selectedMonth,
    dateFrom: handleDateFormating(dataFiltered.dateFrom),
    dateTo: handleDateFormating(dataFiltered.dateTo)
  })

  const invoiceInDefaultStatus = partialPaymentSettings?.status >= milkPaymentStatus.verificationStatus

  const generatingFile = partialPaymentSettings?.status === milkPaymentStatus.generatingStatus

  const {
    data: partialInvoices,
    isLoading: isBusy,
    refetch: fetchInvoices,
    isFetching: isFetchingData
  } = MilkMoneyQuery.getPartialInvoices(
    {
      ...dataFiltered,
      page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1,
      month: selectedMonth,
      dateFrom: handleDateFormating(dataFiltered.dateFrom),
      dateTo: handleDateFormating(dataFiltered.dateTo)
    },
    invoiceInDefaultStatus
  )

  function handleDateFormating(date) {
    return date ? parseDate(date) : null
  }

  useEffect(() => {
    return () => {
      clearInterval(increment.current)
      clearInterval(intervalPdfs.current)
    }
  }, [])

  useEffect(() => {
    fetchInvoices()
  }, [dataFiltered])

  useEffect(() => {
    if (dataFiltered.dateFrom && dataFiltered.dateTo) {
      fetchSettings()
    }
  }, [dataFiltered.dateFrom, dataFiltered.dateTo])

  useEffect(() => {
    setDataFiltered({ ...dataFiltered, search: debouncedValue, page: 0 })
  }, [debouncedValue])

  useEffect(() => {
    if (partialPaymentSettings && selectedMonth === '' && !isFetchingSettings) {
      const selectedMonth = hash !== '' ? hash.toString().split('#')[1] : partialPaymentSettings?.selectedMonth
      setSelectedMonth(selectedMonth)
    }
    if (partialPaymentSettings?.status > milkPaymentStatus.generatingStatus && generating) {
      notify(t('The part payment statements for the month {{selectedMonth}} have been successfully created and can now be verified.', { selectedMonth }), 'success')
      setGenerating(false)
      clearInterval(increment.current)
    } else if (partialPaymentSettings?.status === milkPaymentStatus.generatingStatus && !increment.current) {
      increment.current = setInterval(() => {
        fetchSettings()
      }, 10000)
    }

    //generate pdf files
    if (
      partialPaymentSettings?.status >= milkPaymentStatus.verificationStatus &&
      partialPaymentSettings.generatePdfJob &&
      !intervalPdfs.current
    ) {
      intervalPdfs.current = setInterval(() => {
        fetchSettings()
      }, 10000)
    } else if (partialPaymentSettings?.status >= milkPaymentStatus.verificationStatus &&
      !isFetchingSettings &&
      !partialPaymentSettings?.generatePdfJob &&
      intervalPdfs.current &&
      isLoadingPdfFiles
    ) {
      setIsLoadingPdfFiles(false)
      clearInterval(intervalPdfs.current)
    }
  }, [
    partialPaymentSettings,
    isFetchingSettings,
    hash
  ])

  useEffect(() => {
    fetchSettings()
    fetchInvoices()
  }, [selectedMonth, t])

  function renderMonthFilter() {
    const month = dayjs(selectedMonth).month()
    const lastDay = dayjs(selectedMonth).daysInMonth()
    const minDateFrom = dayjs()
      .set('month', month)
      .set('year', dayjs().year())
      .set('date', 1)
      .format('DD.MM.YYYY')

    const maxDateFrom = `${lastDay}.${month + 1}.${dayjs().year()}`
    const disableFilters = partialPaymentSettings?.status >= milkPaymentStatus.verificationStatus
    const valueDateFrom = disableFilters ? dayjs(partialPaymentSettings?.dateFrom).format('DD.MM.YYYY') : dataFiltered.dateFrom
    const valueDateTo = disableFilters ? dayjs(partialPaymentSettings?.dateTo).format('DD.MM.YYYY') : dataFiltered.dateTo
    const minDateTo = dataFiltered.dateFrom ? getDayAhead(dataFiltered.dateFrom) : minDateFrom

    return <div style={{ display: 'flex', alignItems: 'center', gap: '1em' }}>
      <Dropdown
        selection
        selectOnBlur={false}
        placeholder={t('Select month')}
        value={selectedMonth}
        options={partialPaymentSettings?.months?.map(month => ({ value: month?.key, text: month?.label }))}
        onChange={(e, data) => {
          setDataFiltered({
            ...dataFiltered,
            dateFrom: '',
            dateTo: ''
          })
          setSelectedMonth(data.value as string)
        }}
      />
      <DateInput
        name='date'
        placeholder={t('Date from')}
        disabled={disableFilters}
        minDate={minDateFrom}
        maxDate={dataFiltered.dateTo ? dataFiltered.dateTo : maxDateFrom}
        initialDate={minDateFrom}
        closable
        onKeyDown={(event) => event.preventDefault()}
        markColor={'blue'}
        localization={languageLocal.code}
        closeOnMouseLeave={false}
        dateFormat={'DD.MM.YYYY'}
        className={'datePicker'}
        value={valueDateFrom}
        iconPosition='left'
        onChange={(event, { name, value }) => setDataFiltered({
          ...dataFiltered,
          dateFrom: value
        })}
      />
      <DateInput
        name='date'
        placeholder={t('Date to')}
        minDate={minDateTo}
        disabled={disableFilters}
        maxDate={maxDateFrom}
        initialDate={minDateTo}
        closable
        onKeyDown={(event) => event.preventDefault()}
        markColor={'blue'}
        localization={languageLocal.code}
        closeOnMouseLeave={false}
        dateFormat={'DD.MM.YYYY'}
        className={'datePicker'}
        value={valueDateTo}
        iconPosition='left'
        onChange={(event, { name, value }) => setDataFiltered({
          ...dataFiltered,
          dateTo: value
        })}
      />
    </div>
  }

  function getIcon(value) {
    return <Icon
      name={value ? 'checkmark' : 'close'}
      color={value ? 'green' : 'red'}
    />
  }

  function renderVerificationDetails() {
    return <StyledSegment placeholder>
      <StyledGrid columns={2} stackable>
        <Divider vertical style={{ margin: 0 }} />
        <Grid.Column>
          <Grid columns={2} stackable>
            <CustomRow>
              <Grid.Column>{'Zeitraum Abschlagszahlung'}</Grid.Column>
              <StyledGridColumns>{`${dataFiltered.dateFrom ?? '-'} / ${dataFiltered?.dateTo ?? '-'}`}</StyledGridColumns>
            </CustomRow>
            <StyledGridRow>
              <Grid.Column>{t('Base price deposited?')}</Grid.Column>
              <StyledGridColumns>{getIcon(partialPaymentSettings.basePricesSet)}</StyledGridColumns>
            </StyledGridRow>
          </Grid>
        </Grid.Column>
        <Grid.Column>
          <Grid columns={2} stackable style={{ marginLeft: 5 }}>
            <StyledGridRow>
              <Grid.Column>{t('Delivered Amount (in l)')}</Grid.Column>
              <StyledGridColumns>{formatNumberWithoutFractions(partialPaymentSettings?.totalDeliveredAmountInLiters)}</StyledGridColumns>
            </StyledGridRow>
            <StyledGridRow>
              <Grid.Column>{t('Delivered Amount (in kg)')}</Grid.Column>
              <StyledGridColumns>{formatNumberWithoutFractions(partialPaymentSettings?.totalDeliveredAmountInKg)}</StyledGridColumns>
            </StyledGridRow>
          </Grid>
        </Grid.Column>
      </StyledGrid>
    </StyledSegment>
  }

  function renderGenerateFileLoader() {
    return <>
      <LazyLottie
        getAnimationData={() => import('@assets/json-lottie/file-transfer.json')}
        loop
        id='file-transfer'
        style={{ height: 250, width: 250 }}
      />
      <div style={{ color: 'black' }}>{t('Please be patient, the part payment statements are currently being prepared.')}</div>
    </>
  }

  function renderSteps() {
    return <Step.Group stackable='tablet' columns={2} widths={'5'}>
      {steps.map((step, index) => {
        const lastStep = index === steps.length - 1
        const statusActive = partialPaymentSettings?.status === step.statusValue
        const completedStatus = lastStep ? partialPaymentSettings?.status === step.statusValue : partialPaymentSettings?.status > step.statusValue
        return <Step
          style={{ height: 75 }}
          completed={completedStatus}
          active={statusActive}
          key={step.description}
        >
          {(!statusActive || lastStep) && <Icon
            name={completedStatus ? 'checkmark' : 'close'}
            style={{ color: completedStatus ? 'green' : 'red' }}
          />}
          <Step.Content>
            <Step.Title>{completedStatus ? step.titleAfterFinished : step.title}</Step.Title>
            {statusActive && <StepDescr>
              {step.description}
            </StepDescr>}
          </Step.Content>
        </Step>
      })}
    </Step.Group>
  }

  function renderGenerateMgaPdf(isLoadingFiles) {
    if (isLoadingFiles) {
      return <ProgressWrapper style={{ width: 202, height: 37 }}>
        <Progress
          success
          percent={partialPaymentSettings?.generatePdfJob?.percentage}
        />
      </ProgressWrapper>
    }

    return <Button
      buttonType={'popup'}
      labelPosition='left'
      onClick={() => handleGenerateMgaPdfs()}
      icon
    >
      <Icon name={'server'} style={{ color: 'white' }} />
      {t('Create MGA PDF {{percentage}}', { percentage: isLoadingFiles ? `(${partialPaymentSettings?.generatePdfJob?.percentage} %)` : '' })}
    </Button>
  }

  async function handleGenerateMgaPdfs() {
    try {
      setIsOpenGeneratingPdfs(false)
      setIsLoadingPdfFiles(true)
      await MilkMoneyAccountingService.generatePartialPdfFiles({
        month: selectedMonth
      })
      intervalPdfs.current = setInterval(() => {
        fetchSettings()
      }, 10000)
    } catch (error) {
      setIsLoadingPdfFiles(false)
      clearInterval(intervalPdfs.current)
      handleErrors(error)
    }
  }

  function renderActions() {
    const displayBankFileAction = partialPaymentSettings?.status === milkPaymentStatus.verificationStatus
    const closeMonth = partialPaymentSettings?.status === milkPaymentStatus.preclosingStatus
    const exportFile = partialPaymentSettings?.status >= milkPaymentStatus.preclosingStatus
    const displayGenerateFiles = partialPaymentSettings?.status >= milkPaymentStatus.verificationStatus ?
      (partialPaymentSettings?.file ? partialPaymentSettings?.status < milkPaymentStatus.closedStatus : true) : false

    const displayDownloadAll = partialPaymentSettings?.status >= milkPaymentStatus.verificationStatus && Boolean(partialPaymentSettings?.file)
    const isLoadingFiles = isLoadingPdfFiles || Boolean(partialPaymentSettings?.generatePdfJob)

    return <ActionsWrapper>
      {displayGenerateFiles && renderGenerateMgaPdf(isLoadingFiles)}
      {displayDownloadAll && <ExcelExport
        url={'milk-money/milk-payment/partial/invoices/preview/pdf'}
        fileName={'pdfs'}
        downloadWithFileName
        disabled={isLoadingFiles}
        data={{ month: selectedMonth }}
        icon={'cloud download'}
        name={t('Download MGA PDF')}
      />}
      {displayGenerateButton && <Button
        loading={generating}
        disabled={generating}
        onClick={() => setOpenGeneratingModal(true)}
      >{t('Generate your part payment')}</Button>}
      {displayBankFileAction && <Popup
        content={'Generierung der SEPA XML Datei für alle Milchgeldabrechnungen in diesem Abrechnungsmonat.'}
        trigger={<Button
          loading={generating}
          disabled={generating || Boolean(partialPaymentSettings.hasErrors)}
          buttonType={'popup'}
          labelPosition='left'
          icon
          onClick={() => setExportBankModal(true)}
        >
          <Icon name={'euro sign'} style={{ color: 'white' }} />
          {t('Sepa XML')}
        </Button>
        }
      />}
      {closeMonth && <Button
        onClick={() => setIsOpenCloseMonthDialog(true)}
      >{t('Close billing month')}</Button>}
      {exportFile && <ExcelExport
        url={'milk-money/milk-payment/partial/bank-file/sepa/export'}
        data={{ month: selectedMonth }}
        fileName={'sepa_xml'}
        name={t('Download SEPA XML')}
      />}
    </ActionsWrapper>
  }

  async function handleGenerateInvoice(data?) {
    try {
      setGenerating(true)
      await MilkMoneyAccountingService.generatePartialInvoices({
        month: selectedMonth,
        dateFrom: handleDateFormating(dataFiltered.dateFrom),
        dateTo: handleDateFormating(dataFiltered.dateTo),
        ...data
      })
      setOpenGeneratingModal(false)
      fetchSettings()
      increment.current = setInterval(() => {
        fetchSettings()
      }, 10000)
    } catch (error) {
      setGenerating(false)
      clearInterval(increment.current)
      handleErrors(error)
    }
  }

  function renderGeneratingModal() {
    const monthDate = partialPaymentSettings?.months?.find(month => month.key === selectedMonth)
    return <GenerateMilkBill
      openGeneratingModal={openGeneratingModal}
      handleClose={() => setOpenGeneratingModal(false)}
      selectedMonth={monthDate?.label}
      handleGenerateInvoice={handleGenerateInvoice}
    />
  }

  function renderFilters() {
    return <FilterWrapper>
      <Input
        icon={{ name: 'search', circular: true, link: true }}
        placeholder={t('Search...')}
        value={search}
        onChange={(e, data) => setSearch(data.value)}
      />
    </FilterWrapper>
  }

  async function handleRecalculateInvoiceFile(row, type) {
    setLoadingAction({
      type: type,
      id: row.id,
      value: true,
      idKey: 'id'
    })
    try {
      await MilkMoneyAccountingService.generatePartialInvoice(row.id)
      notify(t('The part payment for the farmer {{supplierNo}} was successfully recalculated and created.', { supplierNo: row?.supplierNo }), 'success')
      fetchSettings()
      fetchInvoices()
    } catch (error) {
      handleErrors(error)
    }
    setLoadingAction({
      type: '',
      id: null,
      value: false,
      idKey: ''
    })
  }

  async function handlePreview(id) {
    setLoadingAction({
      type: 'preview',
      id: id,
      value: true,
      idKey: 'id'
    })
    await fetch(`${ENV.API_URL}/milk-money/milk-payment/partial/invoices/${id}/preview/pdf`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': localStorage.getItem('token') }
      }
    ).then(response => response.blob()).then(response => {
      const csvURL = window.URL.createObjectURL(response);
      setPreviewUrl(csvURL)
    })
    setLoadingAction({
      type: '',
      id: null,
      value: false,
      idKey: ''
    })
  }

  function handleActionClick(row: FarmerInvoiceType, type) {
    switch (type) {
      case 'preview':
        return handlePreview(row.id)
      case 'recalculate':
        return handleRecalculateInvoiceFile(row, type)
      default:
        return
    }
  }

  function renderTable() {
    const editAvailable = partialPaymentSettings?.status === milkPaymentStatus.verificationStatus
    const actionsFiltered = editAvailable ? actions : actions.filter(action => action.type !== 'link').filter(action => action.actionType !== 'recalculate')

    return <>
      <div style={{ marginBottom: 10, marginTop: 20 }}>
        {renderFilters()}
      </div>
      <TableComponent
        isFetchingData={isFetchingData}
        animated
        loading={isFetchingSettings || isBusySettings}
        loadingAction={loadingAction}
        actions={actionsFiltered}
        tableColumnHeaders={tableColumnHeaders}
        dataFiltered={dataFiltered}
        handleClickAction={(row, type) => handleActionClick(row, type)}
        handleChangeDataFiltered={(data, tableColumnHeaders) => {
          setDataFiltered(data)
          setTableColumnHeaders(tableColumnHeaders)
        }}
        rows={rows}
        total={filtered}
      />
    </>
  }

  function renderPreviewModal() {
    return <Dialog
      isDialogOpen={Boolean(previewUrl)}
      textHeader={t('Preview')}
      textClose={t('Close')}
      heightContent={'80vh'}
      type={'preview'}
      textContent={<iframe width='100%' height='100%' src={previewUrl}></iframe>}
      handleClose={() => setPreviewUrl('')}
    />
  }

  async function handleGenerateBankFile(data) {
    setExportBankModal(false)
    setIsBusySepaXML(true)
    try {
      await MilkMoneyAccountingService.generatePartialBankFile({ ...data, month: month.key })
      await fetchSettings()
      notify(t('The SEPA XML file for the month {{month}} has been successfully created and is available for download.', { month: month?.label }), 'success')
    } catch (error) {
      handleErrors(error)
    }
    setIsBusySepaXML(false)
  }

  function renderCloseMonthModal() {
    return <CloseMonth
      isOpenCloseMonthDialog={isOpenCloseMonthDialog}
      handleClose={(submitted?) => {
        if (submitted) {
          fetchSettings()
        }
        setIsOpenCloseMonthDialog(false)
      }}
      selectedMonth={month}
    />
  }

  const { filtered, rows } = partialInvoices ? partialInvoices : { filtered: 0, rows: [] };

  const displayGenerateButton = partialPaymentSettings?.status === milkPaymentStatus.defaultStatus &&
    dataFiltered.dateFrom && dataFiltered.dateTo

  const month = partialPaymentSettings?.months?.find(month => month.key === selectedMonth)

  return <Layout
    title={t('MG-Part payment')}
    filters={partialPaymentSettings && renderMonthFilter()}
    isBusy={isBusySettings || isBusy || isFetchingSettings}
    useSpinner={isFetchingSettings}
    actions={!lighUserLogged && renderActions()}
    segmented
    animatedSpinner={isBusySepaXML ? {
      fileName: 'euro-bank-to-bank-transfer',
      text: t('Please be patient, the SEPA XML file with the booking data is currently being created.')
    } : null}
  >
    {renderCloseMonthModal()}
    <CreateBankFile
      selectedMonth={month}
      openModal={openExportBankModal}
      accounts={partialPaymentSettings?.accounts || []}
      handleClose={(submitted = false) => {
        setExportBankModal(false)
        if (submitted) {
          fetchSettings()
        }
      }}
      handleSubmit={(data) => handleGenerateBankFile(data)}
    />
    {renderPreviewModal()}
    {openGeneratingModal && renderGeneratingModal()}
    <div>
      {renderSteps()}
      {partialPaymentSettings?.status === milkPaymentStatus.defaultStatus && renderVerificationDetails()}

      {(generatingFile || generating) && <div style={{ position: 'relative', height: 'calc(100vh - 700px)' }}>
        <StyledDimmer>
          {renderGenerateFileLoader()}
        </StyledDimmer>
      </div>}
    </div>
    {partialInvoices && invoiceInDefaultStatus && renderTable()}
  </Layout>
}
export default PartPayment